import React from 'react';

import AppButton from '../buttons/AppButton';

import { gridStyleOf, normalizeLayout } from './fieldModel';
import ProcessField from './ProcessField';

import styles from './processFlow.module.scss';

import { ICONS } from '@/constants/icons';

/**
 * Panel for the current stage: renders the fields its definition declares, and
 * the actions that move the process forward.
 *
 * Definition-driven — adding a field to a stage in the admin UI makes it appear
 * here with no code change. If this component ever needs a `switch` on a
 * workflow slug, the abstraction has leaked.
 *
 * Labelled controls throughout; `missing` keys (from `validateStageData`) mark
 * the offending fields with `aria-invalid` and an inline message, instead of one
 * generic error at the top. The `<form>` element is the caller's
 * (`ProcessFlow`), so several panels share one submit scope.
 *
 * @param {Array} props.fields         field descriptors for the current stage
 * @param {object} props.values
 * @param {(key, value) => void} props.onChange
 * @param {string[]} [props.missing]   keys of required fields left empty
 * @param {React.ReactNode} [props.actions]
 * @param {boolean} [props.readOnly]
 * @param {string} [props.title]
 * @param {boolean} [props.eyebrow]    title as a small caps overline, for a
 *   secondary block (i campi comuni) che non deve competere con lo stage
 * @param {string} [props.description]
 * @param {(key: string, files: FileList) => Promise<Array>} [props.onUploadPhoto]
 *   iniettato dal chiamante: il pannello generico non sa dove stia lo Storage
 */
export default function ProcessPanel({
  fields = [],
  values = {},
  onChange,
  missing = [],
  actions,
  readOnly = false,
  title,
  eyebrow = false,
  description,
  onUploadPhoto,
}) {
  const missingSet = new Set(missing);
  // Le coordinate dichiarate sono di fiducia limitata: un tipo scritto a mano o
  // ritoccato può metterne due nella stessa cella, e in CSS grid il secondo si
  // stampa *sopra* il primo. Normalizzare qui è l'unico punto in cui basta.
  const placed = normalizeLayout(fields);

  return (
    <section className={styles.panel}>
      {(title || description) && (
        <header className={styles.panelHeader}>
          {title &&
            (eyebrow ? (
              <h3 className={styles.panelEyebrow}>{title}</h3>
            ) : (
              <h3 className={styles.panelTitle}>{title}</h3>
            ))}
          {description && <p className={styles.panelDescription}>{description}</p>}
        </header>
      )}

      {/* Griglia a 12 colonne: ogni campo si posiziona dove il tipo dichiara.
          Sotto i 700px collassa a colonna singola (vedi il modulo SCSS). */}
      {placed.length > 0 && (
        <div className={styles.panelGrid}>
          {placed.map((field, index) => (
            <div key={field.key} style={gridStyleOf(field, index)} className={styles.gridCell}>
              <ProcessField
                field={field}
                value={values?.[field.key]}
                invalid={missingSet.has(field.key)}
                readOnly={readOnly}
                onChange={onChange}
                onUploadPhoto={onUploadPhoto}
              />
            </div>
          ))}
        </div>
      )}

      {actions && <div className={styles.panelActions}>{actions}</div>}
    </section>
  );
}

/** Default action bar: advance, and optionally close early. */
export function ProcessActions({
  onAdvance,
  onClose,
  advanceLabel,
  closeLabel,
  loading,
  disabled,
}) {
  return (
    <>
      {onClose && (
        <AppButton mode="action" variant="neutral" icon={ICONS.close} onClick={onClose}>
          {closeLabel}
        </AppButton>
      )}
      {onAdvance && (
        <AppButton
          mode="nav"
          variant="primary"
          icon={ICONS.arrowRight}
          onClick={onAdvance}
          loading={loading}
          disabled={disabled}
        >
          {advanceLabel}
        </AppButton>
      )}
    </>
  );
}
